import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { loadBlogPosts } from '../blog';
import { loadProjects } from '../projects';
import { LayoutContext } from '../types/translations';

const Home: React.FC = () => {
  const { texts, isDark, language } = useOutletContext<LayoutContext>();
  const [blogPosts, setBlogPosts] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  // Effect to reload content when language changes
  useEffect(() => {
    const loadContent = async () => {
      // Load blog posts in current language
      const posts = await loadBlogPosts(language);
      const sortedPosts = posts.sort(
        (a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      setBlogPosts(sortedPosts.slice(0, 4));

      // Load projects in current language
      const projData = await loadProjects(language);
      setProjects(projData.slice(0, 2));
    };

    loadContent();
  }, [language]); // Reload when language changes

  // Format date based on current language
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div>
      <main className="container mx-auto px-6 pt-24">
        {/* About Section */}
        <section id="about" className="py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-12">
              <div>
                <h2 className="text-5xl font-bold mb-4">{texts.home.firstTitle}</h2>
                <p className="text-xl text-gray-600 dark:text-gray-300">
                  {texts.home.firstParagraph}
                </p>
              </div>

              <div>
                <h2 className="text-5xl font-bold mb-4">{texts.home.secondTitle}</h2>
                <p className="text-xl text-gray-600 dark:text-gray-300">
                  {texts.home.secondParagraph}
                </p>
              </div>
            </div>
            <div className="relative aspect-square bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-lg overflow-hidden">
              <img
                src="https://res.cloudinary.com/dlplieq2c/image/upload/v1736631420/Anton1Cropped_cy7lhi.jpg"
                alt="Portfolio Hero"
                className={`w-full h-full object-cover transition-all duration-300 ${
                  !isDark ? '' : 'mix-blend-overlay'
                }`}
              />
            </div>
          </div>
        </section>

        {/* Recent Blog Posts */}
        <section id="blog" className="py-20">
          <h2 className="text-4xl font-bold mb-12">
            <Link to="/blog" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              {texts.home.blog}
            </Link>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {blogPosts.map((post, i) => (
              <Link key={i} to={`/blog/${i}`} className="mt-4 block text-black dark:text-white hover:underline transition-colors">
                <article className="flex justify-between items-center bg-white dark:bg-gray-800/30 rounded-lg p-6 shadow-lg hover:shadow-xl dark:hover:bg-gray-800/50 transition-all">
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(post.date)}
                    </p>
                    <h3 className="text-2xl font-bold mb-4">{post.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300">{post.preview}</p>
                  </div>
                  {post.icon && (<img src={post.icon} alt={post.title} className="w-16 h-16 rounded-lg object-cover ml-6"/>)}
                </article>
              </Link>
            ))}
          </div>
        </section>

        {/* Recent Projects Preview */}
        <section id="projects" className="pb-20">
          <h2 className="text-4xl font-bold mb-12">
            <Link to="/projects" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              {texts.home.projects}
            </Link>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((proj, i) => (
              <Link key={i} to={`/projects/${i}`} className="mt-4 block text-black dark:text-white hover:underline transition-colors">
                <article className="flex justify-between items-center bg-white dark:bg-gray-800/30 rounded-lg p-8 shadow-lg hover:shadow-xl dark:hover:bg-gray-800/50 transition-all">
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(proj.date)}
                    </p>
                    <h3 className="text-2xl font-bold mb-4">{proj.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300">{proj.preview}</p>
                  </div>
                  {proj.icon && (<img src={proj.icon} alt={proj.title}  className="w-20 h-20 rounded-lg object-cover ml-6" />)}
                </article>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;
